import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { api, fmtMoney, SQUARE_APP_ID, SQUARE_LOCATION_ID } from "@/lib/api";
import { ShieldCheck, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STORE_ADDRESS, STORE_PHONE } from "@/constants/store";

export default function PayLinkPage() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api
      .get(`/paylinks/${token}`)
      .then((r) => setLink(r.data))
      .catch(() => setError("This payment link doesn't exist. Please call the store at " + STORE_PHONE));
  }, [token]);

  const onTokenize = async (t) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/paylinks/${token}/pay`, {
        source_id: t.token,
        customer_email: email || null,
      });
      setDone(data);
      toast.success("Payment successful!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#0A1C40] px-4 py-8" data-testid="paylink-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="IPVALUE MARKETPLACE" className="h-14 mx-auto" />
          <div className="text-white/60 text-xs mt-2">{STORE_ADDRESS.short}</div>
        </div>
        <div className="bg-white rounded-lg p-6">{children}</div>
        <div className="text-center text-white/50 text-xs mt-4">
          Secure payment by Square · Questions? Call {STORE_PHONE}
        </div>
      </div>
    </div>
  );

  if (error) return <Shell><div className="text-center text-sm py-8" data-testid="paylink-error">{error}</div></Shell>;
  if (!link) return <Shell><div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></Shell>;

  if (done || link.paid) {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="paylink-paid">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
          <h1 className="font-display font-black text-2xl mt-4">Payment received!</h1>
          <p className="text-sm text-gray-600 mt-2">
            Order <b>{link.order_number}</b> is paid. Thank you!
          </p>
          {done?.receipt_url && (
            <a href={done.receipt_url} target="_blank" rel="noreferrer" className="inline-block mt-4 text-sm underline text-[#0A1C40]" data-testid="paylink-receipt-link">
              View Square receipt →
            </a>
          )}
        </div>
      </Shell>
    );
  }

  if (link.expired || link.status === "cancelled") {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="paylink-expired">
          <Clock className="w-12 h-12 text-amber-500 mx-auto" />
          <h1 className="font-display font-black text-xl mt-4">
            {link.status === "cancelled" ? "This order was cancelled" : "This link has expired"}
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Please call us at <a className="font-bold underline" href={`tel:+1${STORE_PHONE.replace(/\D/g, "")}`}>{STORE_PHONE}</a> and we'll send you a fresh link.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="paylink-details">
        <div className="ipv-eyebrow text-[#0A1C40]">Order {link.order_number}</div>
        <h1 className="font-display font-black text-2xl mt-1">
          Hi {(link.customer_name || "there").split(" ")[0]}, {link.is_hold_balance ? "your balance is" : "your total is"} {fmtMoney(link.is_hold_balance ? link.balance_due : link.total)}
        </h1>
        {link.is_hold_balance && (
          <div className="mt-2 text-xs bg-blue-50 border border-[#0A1C40] px-3 py-2" data-testid="paylink-hold-note">
            Your <b>$50 deposit</b> is already applied — this payment completes your held order.
          </div>
        )}
        <div className="mt-4 space-y-1.5 text-sm border-y border-gray-200 py-3">
          {link.line_items.map((li, i) => (
            <div key={i} className="flex justify-between gap-2">
              <span className="flex-1">{li.quantity} × {li.name}{li.color ? ` · ${li.color}` : ""}</span>
              <span className="font-bold">{fmtMoney(li.line_total)}</span>
            </div>
          ))}
          <div className="flex justify-between text-gray-500 pt-2"><span>Subtotal</span><span>{fmtMoney(link.subtotal)}</span></div>
          <div className="flex justify-between text-gray-500"><span>Tax (6%)</span><span>{fmtMoney(link.tax)}</span></div>
          <div className="flex justify-between font-black text-lg"><span>Total</span><span data-testid="paylink-total">{fmtMoney(link.total)}</span></div>
        </div>
        <input
          className="input-base mt-4"
          type="email"
          placeholder="Email for your receipt (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="paylink-email-input"
        />
        <div className="mt-4">
          <div className="ipv-eyebrow mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#0A1C40]" /> Pay securely
          </div>
          {submitting && (
            <div className="mb-3 text-sm flex items-center gap-2 text-[#0A1C40]">
              <Loader2 className="w-4 h-4 animate-spin" /> Processing…
            </div>
          )}
          <PaymentForm applicationId={SQUARE_APP_ID} locationId={SQUARE_LOCATION_ID} cardTokenizeResponseReceived={onTokenize}>
            <CreditCard
              buttonProps={{
                isLoading: submitting,
                css: { backgroundColor: "#0A1C40", fontWeight: 700, borderRadius: "9999px" },
              }}
            >
              Pay {fmtMoney(link.is_hold_balance ? link.balance_due : link.total)}
            </CreditCard>
          </PaymentForm>
        </div>
      </div>
    </Shell>
  );
}
